import { useVerifyActionOnFlags } from '@/hooks';
import { Accordion, Box, Card, Icon } from '@palmetto/palmetto-components';
import { FormikValues } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { SetStateAction, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AllDocumentTypes,
  documentTitles,
  Flag,
  FlagHistoryStatus,
  FlagPurposeType,
  FlagTypeLabels,
  MilestoneLabels,
  MilestonePackageRoute,
  MilestoneType,
  NON_SPECIAL_PACKAGE_REVIEWED_STATUSES,
  PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES,
  QueueDocumentResponse,
  QueueHistoryAction,
  QueueStatus,
  QueueType,
} from 'types';

import { useAuth } from './auth/authProvider';
import { isPalmettoFinanceUser } from './auth/RequirePalmettoFinanceUser';
import { ActionListFooter } from './MilestonePackagePartials/ActionListFooter';
import { ActionListFooterRHF } from './MilestonePackagePartials/ActionListFooterRHF';
import { QueueFlagsContext } from './QueueFlags/QueueFlagsContext';

type ActionsListProps = {
  milestoneType: MilestoneType;
  accountId: string;
  canSubmitPackage?: boolean;
  isLoading?: boolean;
  isFirstSubmission?: boolean;
  canSubmitReview?: boolean;
  onSave?: (data: { accountId: string; data: FormikValues }) => Promise<void>;
  disabled?: boolean;
  disableApproval?: boolean;
  initialValues?: FormikValues;
  onSubmit?: (values: FormikValues, { setSubmitting }: any) => Promise<void>;
  customSummaryLabel?: string;
  nonContextFlags?: Flag[];
  showActionListFooter?: boolean;
  useRHFFooter?: boolean;
  conditionallyApprovedFlags?: Flag[];
  queue?: QueueDocumentResponse;
  subQueueInfo?: {
    type: QueueType | undefined;
    status: QueueStatus | undefined;
  };
};

const flagCategories = [
  FlagPurposeType.rejection,
  FlagPurposeType.stipulation,
  FlagPurposeType.additionalReview,
  FlagPurposeType.changeOrder,
];
const numberOfIssuesToShow = 2;

const flagPurposeBackgroundMap = {
  [FlagPurposeType.rejection]: 'danger',
  [FlagPurposeType.stipulation]: 'warning',
  [FlagPurposeType.additionalReview]: 'tertiary',
  [FlagPurposeType.changeOrder]: 'info',
} as const;

export function useShouldRenderActionsList({
  flagDrivenPackageReview,
  isFinCoUser,
  queue,
  flagsByPurposeType,
}: {
  flagDrivenPackageReview: boolean;
  isFinCoUser: boolean;
  queue?: QueueDocumentResponse;
  flagsByPurposeType: Record<string, Flag[]>;
}): boolean {
  return useMemo(() => {
    const hasOnlyNotificationOrAdditionalReviewFlags = Object.entries(flagsByPurposeType)
      .filter(([_key, flags]) => flags.length > 0)
      .every(([purpose]) => purpose === FlagPurposeType.additionalReview);

    const shouldHideActionList = !isFinCoUser && hasOnlyNotificationOrAdditionalReviewFlags;

    const queueStatus = queue?.status;
    if (!flagDrivenPackageReview) {
      return false;
    }

    if (queueStatus && PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES.has(queueStatus) && !isFinCoUser) {
      if (
        !queue?.history?.find(
          (item) =>
            item?.action === QueueHistoryAction.status && NON_SPECIAL_PACKAGE_REVIEWED_STATUSES.has(item?.changedTo),
        )
      ) {
        return false;
      }
    }

    if (shouldHideActionList) {
      return false;
    }

    return true;
  }, [flagDrivenPackageReview, isFinCoUser, queue, flagsByPurposeType]);
}

export const ActionsList = ({
  milestoneType,
  accountId,
  canSubmitPackage = false,
  isLoading,
  isFirstSubmission,
  canSubmitReview = false,
  onSave,
  disabled,
  disableApproval,
  initialValues,
  onSubmit,
  customSummaryLabel,
  nonContextFlags,
  showActionListFooter = true,
  useRHFFooter = false,
  conditionallyApprovedFlags,
  queue,
  subQueueInfo,
}: ActionsListProps) => {
  const { flagDrivenPackageReview } = useFlags();
  const { getAllFlags, getVisibleFlags } = useContext(QueueFlagsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { action } = useParams();
  const milestonePackageURI = location.pathname.split('/')[3];
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);

  const visibleFlags = useMemo(() => {
    const allFlags = nonContextFlags ? nonContextFlags : getAllFlags ? getAllFlags(true) : [];
    return nonContextFlags ? nonContextFlags : getVisibleFlags(allFlags);
  }, [getAllFlags, getVisibleFlags, nonContextFlags]);

  const flagsByPurposeType = useMemo(() => {
    function getIntendedPurpose(purpose: FlagPurposeType) {
      return nonContextFlags ? purpose === FlagPurposeType.stipulation : purpose === FlagPurposeType.rejection;
    }
    return flagCategories.reduce(
      (acc, purpose) => {
        const filteredFlags = visibleFlags?.filter(
          (flag: Flag) =>
            ((!flag.purposeType || !flag.flagType) && getIntendedPurpose(purpose)) ||
            (flag.purposeType === purpose && flag.flagType),
        );

        acc[purpose] =
          filteredFlags?.toSorted((a, b) => {
            const aResolved = a.resolved === true ? 1 : 0;
            const bResolved = b.resolved === true ? 1 : 0;
            return aResolved - bResolved;
          }) || [];

        return acc;
      },
      {} as Record<string, Flag[]>,
    );
  }, [nonContextFlags, visibleFlags]);

  const { allRejectionFlagsHaveActionTaken } = useVerifyActionOnFlags(visibleFlags);

  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({
    rejection: true,
    stipulation: true,
    additionalReview: true,
  });

  const shouldRender = useShouldRenderActionsList({
    flagDrivenPackageReview,
    isFinCoUser,
    queue,
    flagsByPurposeType,
  });

  if (!shouldRender) {
    return null;
  }

  const handleFlagClick = (id: string, flagId: string) => {
    if (nonContextFlags) {
      navigate(
        `/accounts/${id}/${MilestonePackageRoute.installPackage}/flags/${flagId}/${milestonePackageURI}/${action}`,
      );
    } else {
      navigate(`/accounts/${id}/${milestonePackageURI}/flags/${flagId}`);
    }
  };

  const renderDetails = (
    purpose: FlagPurposeType,
    isOpen: boolean,
    setOpenActionPanels: (value: SetStateAction<Record<string, boolean>>) => void,
  ) => {
    let cursorStyle: { cursor: string }, handleActionPanelsToggle: () => void;
    const count = flagsByPurposeType[purpose]?.length || 0;
    const isToggled = isOpen && count > 0;
    handleActionPanelsToggle = () => setOpenActionPanels((prev) => ({ ...prev, [purpose]: !isOpen }));
    cursorStyle = { cursor: 'pointer' };
    return count > 0 ? (
      <Accordion>
        <Accordion.Panel isOpen={isToggled}>
          <Accordion.PanelSummary
            onToggle={handleActionPanelsToggle}
            isDetailsOpen={isOpen}
            background={flagPurposeBackgroundMap[purpose as keyof typeof flagPurposeBackgroundMap]}
          >
            <Box display="flex" direction="row" justifyContent="space-between">
              <Box as="h3" fontSize="lg" fontWeight="regular" direction="row">
                {customSummaryLabel
                  ? customSummaryLabel
                  : purpose === FlagPurposeType.stipulation
                    ? `No Action Needed For ${MilestoneLabels[milestoneType]}`
                    : purpose === FlagPurposeType.additionalReview
                      ? 'Additional Review'
                      : `Action Required For ${MilestoneLabels[milestoneType]}`}{' '}
                &#8729;&nbsp; <Box fontWeight="regular">{count}</Box>
              </Box>
            </Box>
          </Accordion.PanelSummary>
          <Accordion.PanelDetails padding="0 md">
            <Box background="primary">
              <Box>
                {flagsByPurposeType[purpose].map((flag, index) => {
                  const isResolved = flag.resolved === true;
                  const flagProblemsAsText = flag?.problems?.map(
                    (problem, i) =>
                      `${problem.displayText}${flag?.problems && i !== flag?.problems?.length - 1 ? '; ' : ''}`,
                  );
                  const typeLabelKey = flag?.flagType || flag?.type;
                  const typeLabel = typeLabelKey
                    ? `${FlagTypeLabels[typeLabelKey as keyof typeof FlagTypeLabels] || documentTitles[typeLabelKey as AllDocumentTypes]}:`
                    : `${flag?.category || flag?.dataKey}:`;

                  const flagAction =
                    flag?.actionHistory && flag.actionHistory.length > 0
                      ? flag.actionHistory[flag.actionHistory.length - 1]
                      : undefined;
                  const actionTaken = flagAction?.flagSubject;
                  const actionStatus = flagAction?.actionStatus;
                  const resolvedFlagAction = flag?.actionHistory?.find(
                    (resolvedAction) => resolvedAction?.actionStatus === FlagHistoryStatus.resolved,
                  );
                  const flagIsEditable = flag?.id && flag?.flagType;
                  let textDecoratorStyle: { textDecoration?: string }, handleFlagClickEvent: () => void;
                  textDecoratorStyle = isResolved ? { textDecoration: 'line-through' } : {};
                  handleFlagClickEvent = () => {
                    if (flag.id) {
                      handleFlagClick(accountId, flag.id.toString());
                    }
                  };
                  return (
                    <Box
                      key={
                        flag.id?.toString() ??
                        `${purpose}-${flag.flagType ?? flag.type ?? flag.category ?? flag.dataKey}`
                      }
                      direction="row"
                      justifyContent="space-between"
                      borderColor="separator"
                      borderWidth={`0 0 ${index !== count - 1 ? 'xs' : '0'} 0`}
                      alignItems="center"
                    >
                      <Box fontSize="md" padding="md" direction="row" childGap="sm">
                        {isResolved && <Icon name="c-check" />}
                        <Box direction="row" childGap="sm" style={textDecoratorStyle}>
                          <Box fontWeight="medium" as="span">
                            {typeLabel}
                          </Box>
                          <Box fontWeight="light" as="span">
                            {flag?.reason
                              ? flag.reason
                              : flagProblemsAsText && flagProblemsAsText.length <= numberOfIssuesToShow
                                ? flagProblemsAsText
                                : 'Multiple issues found'}
                          </Box>
                        </Box>
                      </Box>
                      {flagIsEditable ? (
                        <Box
                          padding="sm 0"
                          alignItems="center"
                          justifyContent="space-between"
                          childGap="xs"
                          style={cursorStyle}
                          direction="row"
                          onClick={handleFlagClickEvent}
                        >
                          <Box childGap="xs" alignItems="flex-end">
                            <Box direction="row" childGap="xs">
                              <Icon name="flag-unselected" />
                              {actionTaken && (
                                <Box direction="row" childGap="xs">
                                  <Icon name="arrow-right" />
                                  <Icon name="file-upload" />
                                </Box>
                              )}
                              {isResolved && (
                                <Box direction="row" childGap="xs">
                                  <Icon name="arrow-right" />
                                  <Icon name="flag-resolved" color="success" />
                                </Box>
                              )}
                            </Box>
                            <Box>
                              {isResolved
                                ? `Resolved${
                                    resolvedFlagAction?.date
                                      ? ` ${DateTime.fromISO(resolvedFlagAction.date.toString()).toLocaleString(DateTime.DATETIME_SHORT)}`
                                      : ''
                                  }`
                                : actionStatus === FlagHistoryStatus.resolved ||
                                    actionStatus === FlagHistoryStatus.issueAddressed ||
                                    actionStatus === FlagHistoryStatus.modified
                                  ? 'Ready for review'
                                  : 'Not resolved'}
                            </Box>
                          </Box>
                          <Box>
                            <Icon name="caret-right" />
                          </Box>
                        </Box>
                      ) : (
                        <Box textAlign="right" padding="0 lg 0 0">
                          {isResolved ? 'Resolved' : nonContextFlags ? 'Not Resolved' : 'See below'}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Accordion.PanelDetails>
        </Accordion.Panel>
      </Accordion>
    ) : null;
  };

  let subQueueInfoValues: { type: QueueType | undefined; status: QueueStatus | undefined };
  subQueueInfoValues = {
    type: subQueueInfo?.type,
    status: subQueueInfo?.status,
  };

  const ActionFooter = useRHFFooter ? ActionListFooterRHF : ActionListFooter;

  const installPackageSubmissionValidation = canSubmitPackage && allRejectionFlagsHaveActionTaken;
  const hasRejectionFlags = flagsByPurposeType[FlagPurposeType.rejection]?.length > 0;
  const hasStipulationFlags = flagsByPurposeType[FlagPurposeType.stipulation]?.length > 0;
  const hasAdditionalReviewFlags = flagsByPurposeType[FlagPurposeType.additionalReview]?.length > 0;

  const shouldShowCard = hasRejectionFlags || hasStipulationFlags || hasAdditionalReviewFlags;
  return (
    shouldShowCard && (
      <Box padding={'0 0 lg 0'}>
        <Card>
          <Box
            background="primary"
            padding={openPanels.rejection ? 'xs' : openPanels.stipulation ? 'xs' : 'md'}
            childGap="sm"
          >
            {renderDetails(FlagPurposeType.rejection, openPanels.rejection, setOpenPanels)}
            {renderDetails(FlagPurposeType.stipulation, openPanels.stipulation, setOpenPanels)}
            {isFinCoUser &&
              renderDetails(FlagPurposeType.additionalReview, openPanels.additionalReview, setOpenPanels)}
            {showActionListFooter && initialValues && onSubmit && (
              <ActionFooter
                canSubmitPackage={installPackageSubmissionValidation}
                isLoading={isLoading ?? false}
                canSubmitReview={canSubmitReview ?? false}
                onSave={onSave!}
                disabled={disabled || !allRejectionFlagsHaveActionTaken}
                disableApproval={disableApproval}
                actionButtonText={
                  allRejectionFlagsHaveActionTaken
                    ? isFirstSubmission
                      ? 'Ready to Submit'
                      : 'Ready to Resubmit'
                    : isFirstSubmission
                      ? 'Submit for Review'
                      : 'Resubmit for Review'
                }
                conditionallyApprovedFlags={conditionallyApprovedFlags}
                subQueueInfo={subQueueInfoValues}
              />
            )}
          </Box>
        </Card>
      </Box>
    )
  );
};
